<template>
  <div :class="{ 'ring-offset-2 ring-primary-200 ring-4 rounded-md dark:ring-primary-800 dark:ring-offset-gray-900' : isActive }">
    <template v-if="isEnabled">
      <div class="w-full border px-3 py-3 rounded-md bg-gray-50 dark:bg-gray-900">
        <div v-if="_staging.recurrence.enabled">
          <div v-if="label && (!hasGroupService || isCustomerTypeGroup)" class="w-full rounded bg-primary-100 p-2 mb-5 text-center dark:bg-primary-800/20">
            <p class="text-sm text-primary-700 dark:text-primary-300">
              {{ label }}
            </p>
          </div>

          <recur-reservation
            v-if="!hasGroupService || isCustomerTypeGroup"
            v-model:recurrence="_staging.recurrence.recurrence"
            v-model:preview="_staging.recurrence.preview"
            :editable="!isMove"
          />
          <recur-customer
            v-else
            :reservation="reservation"
            v-model:reservations="_staging.recurrence.reservations"
            v-model:customer="_staging.customer"
            :editable="!isMove"
            auto
          />
        </div>
      </div>
    </template>

    <template v-else>
      <div class="bg-gray-50 border text-center rounded p-4 dark:bg-gray-900">
        <p class="text-gray-600 text-sm dark:text-gray-300">
          {{ $t('components.reservation.create.recurrence.select') }}
        </p>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'

import RecurReservation from '@/components/recurrence/elements/Reservation.vue'
import RecurCustomer from '@/components/recurrence/elements/Customer.vue'

import {
  ToggleField,
} from '@/components/fields'

import { ConfirmationRequestService } from '@/_api/models/booking'
import { RecurrenceRequest } from '@/_api/models/recurrence'

const { $api, $notification, $i18n } = useNuxtApp()

interface Props {
  staging: any
  state: string
  isActive: boolean
}

interface Emits {
  (e: 'update:staging', value: any): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const label = ref<string | boolean>(false)

const _staging = computed({
  get () {
    return props.staging
  },
  set (value) {
    emit('update:staging', value)
  },
})

const reservation = computed(() => {
  const availabilitySlot = _staging.value?.availabilitySlot
  const service = availabilitySlot?.services[0]
  return service?.reservation
})

const isEnabled = computed(() => {
  return !!props.staging.validation
})

const isMove = computed(() => {
  return props.staging?.move
})

const isCustomerTypeGroup = computed(() => {
  return props.staging?.isCustomerTypeGroup
})

const hasGroupService = computed(() => {
  return props.staging?.hasGroupService
})

const fetchPreview = debounce(async () => {
  label.value = $i18n.t('components.reservation.create.recurrence.loading') as string

  const availability = _staging.value.availabilitySlot
  const recurrence = _staging.value.recurrence

  if (availability?.services && recurrence?.enabled && (!hasGroupService.value || isCustomerTypeGroup.value)) {
    try {
      const reservationMove = props.staging.move
      const recurrenceData = reservationMove ? null : props.staging.recurrence.recurrence
      const services = availability.services.map((reservationService: any) => ConfirmationRequestService.fromSlotService(reservationService, availability.date))

      const request = RecurrenceRequest.create({ recurrence: recurrenceData, services, reservation: reservationMove })
      const response = await $api.booking.previewRecurrence(request)

      if (response.data) {
        label.value = request.recurrence?.label || null
        _staging.value.recurrence.preview = response.data
      }
    } catch (e: any) {
      console.log(e)
      label.value = false
      $notification.error({ errors: e?.errors })
    }
  }
}, 1000)

watch(() => _staging.value.availabilitySlot, (availabilitySlot) => {
  const date = availabilitySlot?.date

  if (date && _staging.value.recurrence) {
    _staging.value.recurrence.setDayOfWeek(date)
  }

  fetchPreview()
}, { deep: true })

watch(() => _staging.value.recurrence?.recurrence, () => {
  fetchPreview()
}, { deep: true })

watch(() => _staging.value.recurrence?.enabled, () => {
  fetchPreview()
})

onMounted(() => {
  fetchPreview()
})
</script>
